/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useContext, useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import useHybridURL from '../../hooks/use-hybrid-url'
import useEffectEvent from '../../hooks/use-effect-event'
import {useCommerceAPI, CustomerContext} from '../contexts'
import {MY_ACCOUNT_ORDERS_LIMIT} from '../../constants'
import {getAddressIdentifier} from '../../utils/address-utils'
import {removeSiteLocaleFromPath, getParameterValue} from '../../utils/url'
import {LOGIN_INACTIVE_ERROR_MESSAGE} from '../../constants'
import useOcapiSite from './useOcapiSite'

const AuthTypes = Object.freeze({GUEST: 'guest', REGISTERED: 'registered'})

// This value represents the max age in milliseconds a customer can be before they are
// no longer considered a "new" customer.
// E.g. If a customers creation date is older than 2 seconds it will no longer be considered
// a new customer.
const NEW_CUSTOMER_MAX_AGE = 2 * 1000 // 2 seconds in milliseconds

export default function useCustomer() {
    const api = useCommerceAPI()
    const location = useLocation()
    const hybridURL = useHybridURL()
    const {customPreferences} = useOcapiSite()
    const {customer, setCustomer} = useContext(CustomerContext)

    const onLogin = useEffectEvent(async (credentials, logout) => {
        const skeletonCustomer = await api.auth.login(credentials)
        if (skeletonCustomer.authType === 'guest') {
            return skeletonCustomer
        } else {
            const customer = await api.shopperCustomers.getCustomer({
                parameters: {customerId: skeletonCustomer.customerId}
            })
            const activationToken = decodeURIComponent(getParameterValue(location, 'token'))
            if (customer.c_registrationToken === activationToken) {
                // activate customer
                const body = {c_activated: true}
                const activatedCustomer = await api.shopperCustomers.updateCustomer({
                    body,
                    parameters: {customerId: customer.customerId}
                })
                return activatedCustomer
            } else if (customPreferences.doubleOptInEnabled && !customer.c_activated) {
                await logout()
                return Promise.reject({
                    message: LOGIN_INACTIVE_ERROR_MESSAGE,
                    isCustomerInactive: true
                })
            } else {
                return customer
            }
        }
    })
    const onLogout = useEffectEvent(() => {
        const reloadURL = removeSiteLocaleFromPath(location.pathname)
        hybridURL(reloadURL, reloadURL, {
            message: LOGIN_INACTIVE_ERROR_MESSAGE
        })
    })

    const self = useMemo(() => {
        return {
            ...customer,

            /**
             * Returns boolean value whether the user data is initialized
             */
            get isInitialized() {
                return !!customer?.customerId
            },

            /**
             * Returns boolean value whether the customer is of type `registered` or not.
             */
            get isRegistered() {
                return customer?.authType === AuthTypes.REGISTERED
            },

            /**
             * Returns boolean value whether the customer is of type `guest` or not.
             */
            get isGuest() {
                return customer?.authType === AuthTypes.GUEST
            },

            /**
             * Returns if this customer is newly registered.
             */
            get isNew() {
                if (!customer || customer.authType !== 'registered') return false
                const lastLoginTimeStamp = Date.parse(customer.lastLoginTime)
                const creationTimeStamp = Date.parse(customer.creationDate)
                return lastLoginTimeStamp - creationTimeStamp < NEW_CUSTOMER_MAX_AGE
            },

            /** Returns the customer's saved addresses with the 'preferred' address in the first index */
            get addresses() {
                if (!customer?.addresses) {
                    return undefined
                }
                const preferredAddressIndex = customer.addresses.find((addr) => addr.preferred)
                if (preferredAddressIndex > -1) {
                    return [
                        customer.addresses[preferredAddressIndex],
                        customer.addresses.slice(preferredAddressIndex, preferredAddressIndex + 1)
                    ]
                }
                return customer.addresses
            },

            async accountExists(email) {
                const response = await api.customObjects.getCustomers({
                    parameters: {email: email}
                })
                return response.c_result.exists
            },

            async subscribeToNewsletter(...args) {
                // response doesn't have status so if request was success
                // the subscription should also be successful

                const {
                    email,
                    firstName,
                    lastName,
                    billingCity,
                    billingCountry,
                    billingPostalCode,
                    billingStreet,
                    gRecaptchaResponse,
                    optIn,
                    source
                } = args[0]

                await api.customObjects.subscribeToNewsletter({
                    parameters: {
                        email: email,
                        firstName: firstName,
                        lastName: lastName,
                        billingCity: billingCity,
                        billingCountry: billingCountry,
                        billingPostalCode: billingPostalCode,
                        billingStreet: billingStreet,
                        optIn: optIn,
                        gRecaptchaResponse,
                        source
                    }
                })
                return true
            },

            /**
             * Log in customer account.
             *
             * @param {object} credentials
             * @param {string} credentials.email
             * @param {string} credentials.password
             */
            async login(credentials) {
                const customer = await onLogin(credentials, self.logout)
                setCustomer(customer)
            },

            /**
             * Log out current customer.
             * and retrieve a guest access token
             */
            async logout() {
                const customer = await api.auth.logout()
                setCustomer(customer)
            },

            /**
             * Fetch current customer information.
             */
            async getCustomer() {
                const result = await api.shopperCustomers.getCustomer({
                    parameters: {customerId: customer.customerId}
                })
                if (!customPreferences.doubleOptInEnabled || result.c_activated) {
                    setCustomer(result)
                } else {
                    await self.logout()
                    onLogout()
                }
            },

            /**
             * Register a new customer account.
             *
             * @param {object} data
             * @param {boolean} data.acceptsMarketing - A boolean indicates whether customer accept marketing emails.
             * @param {string} data.email
             * @param {string} data.firstName
             * @param {string} data.lastName
             * @param {string} data.password
             * @param {boolean} login - Indicate if login flow should be triggered after registration
             */
            async registerCustomer(data, {login = true} = {}, registerCustomerCallback) {
                // TODO: investigate how to submit/include the checkbox input
                // for receiving email communication.

                const body = {
                    customer: {
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        login: data.email
                    },
                    password: data.password
                }

                const response = await api.shopperCustomers.registerCustomer({body})
                // Check for error json response
                if (response.detail && response.title && response.type) {
                    throw new Error(response.detail)
                }

                if (registerCustomerCallback && response.customerNo) {
                    await registerCustomerCallback(response.customerNo)
                }

                if (login) {
                    // Send a new login request with the given credentials to ensure tokens are updated.
                    await self.login({email: data.email, password: data.password})
                } else {
                    await self.logout()
                }
            },

            /**
             * Update customer information.
             *
             * @param {object} data
             * @param {string} data.salutation
             * @param {string} data.email
             * @param {string} data.firstName
             * @param {string} data.lastName
             * @param {string} data.phone
             * @param {string} data.birthday
             * @param {string} data.c_children
             */
            async updateCustomer(data) {
                const body = {
                    salutation: data.salutation,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phoneHome: data.phone,
                    birthday: data.birthday,
                    c_children: data.c_children,

                    // NOTE/ISSUE
                    // The sdk is allowing you to change your email to an already-existing email.
                    // I would expect an error. We also want to keep the email and login the same
                    // for the customer, but the sdk isn't changing the login when we submit an
                    // updated email. This will lead to issues where you change your email but end
                    // up not being able to login since 'login' will no longer match the email.
                    email: data.email,
                    login: data.email
                }

                const response = await api.shopperCustomers.updateCustomer({
                    body,
                    parameters: {customerId: customer.customerId}
                })

                // Check for error json response
                if (response.detail && response.title && response.type) {
                    throw new Error(response.detail)
                }

                // This previous request does return the updated customer profile, however it does
                // not include the 'entire' customer. It is missing address and payment methods.
                // We need to refetch the customer to make sure everything is up to date.
                await self.getCustomer()
            },

            /**
             * Update customer password.
             *
             * @param {object} data
             * @param {string} data.currentPassword - The old password.
             * @param {string} data.password - The new password.
             * @param {string} email - Customer's email
             */
            async updatePassword(data, email) {
                const body = {
                    password: data.password,
                    currentPassword: data.currentPassword
                }

                // Note that we're using the raw response here. This request does not return
                // data when successful, but the sdk tries to parse the json (potential sdk bug).
                const rawResponse = await api.shopperCustomers.updateCustomerPassword(
                    {
                        body,
                        parameters: {customerId: customer.customerId}
                    },
                    true
                )

                // Success has no json response to parse, but errors do, so handle that here.
                if (rawResponse.status >= 400) {
                    const json = await rawResponse.json()
                    // Check for error json response
                    if (json.detail && json.title && json.type) {
                        throw new Error(json.detail)
                    }
                }

                // Fetch a new SLAS JWT to update the invalid one in client app state
                const credentials = {
                    email: email,
                    password: data.password
                }

                await self.login(credentials)
            },

            /**
             * @todo - Backend set up required
             * @param {string} login - customer email address
             */
            async getResetPasswordToken(login) {
                // this is not working at the moment
                // @see https://github.com/SalesforceCommerceCloud/pwa-kit/issues/363
                // we use OCAPI passwordReset
                // const response = await api.shopperCustomers.getResetPasswordToken({body: {login}})
                const response = await api.shopperCustomersOcapi.passwordReset({
                    body: {
                        identification: login,
                        type: 'email'
                    }
                })

                // Check for error json response
                if (response.detail && response.title && response.type) {
                    throw new Error(response.detail)
                }
            },

            /**
             * Calls subscribe for newsletter/notification SFCC endpoint (custom object).
             *
             * @param {object} data is an object with all the data that would be stored as a notification object
             * @param {string} data.email is the mail of the customer that would be notified
             * @param {string} data.firstName is the first name of the customer that would be notified
             * @param {string} data.lastName is the last name of the customer that would be notified
             * @param {string} data.phone is the phone of the customer that would be notified
             */
            async subscribeToProductNotification(...args) {
                const {email, firstName, gRecaptchaResponse, lastName, phone} = args[0]
                await api.customObjects.subscribeToNewsletter({
                    parameters: {
                        email: email,
                        firstName: firstName,
                        gRecaptchaResponse,
                        lastName: lastName,
                        phone: phone,
                        source: 'Web - Notify'
                    }
                })
                return true
            },

            /**
             * Add a new saved address.
             *
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
             */
            async addSavedAddress(address) {
                // The `addressId` field is required to save the customer's address to their account.
                // There are some case when it is missing as during the process of creation of an order address
                // where `addressId` is replaced with `id` and in case it is missing we try to create a new unique one
                // that is a combination of `city` and `address1` and possible an incremental suffix
                // Rather than make the customer provide a name/id, we can generate a unique id behind
                // the scenes instead. This is only useful if you are not displaying the addr name/id
                // in the UI, which we aren't.
                const addressId = `${address.city} ${address.address1}`

                // address has address ID so it has to be updated not created
                if (address.addressId) {
                    await self.updateSavedAddress(address)
                    return
                }

                const body = {
                    ...address,
                    addressId: getAddressIdentifier(customer, addressId)
                }

                await api.shopperCustomers.createCustomerAddress({
                    body,
                    parameters: {customerId: customer.customerId}
                })

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer()
            },

            /**
             * Update a saved address.
             *
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
             */
            async updateSavedAddress(address) {
                const body = address

                await api.shopperCustomers.updateCustomerAddress({
                    body,
                    parameters: {customerId: customer.customerId, addressName: address.addressId}
                })

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer()
            },

            /**
             * Add a new saved payment instrument.
             *
             * @external PaymentInstrument
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customerpaymentinstrumentrequest
             */
            async addSavedPaymentInstrument(paymentInstrument) {
                const body = {
                    bankRoutingNumber: '',
                    giftCertificateCode: '',
                    ...paymentInstrument,
                    paymentCard: {
                        ...paymentInstrument.paymentCard,
                        securityCode: undefined
                    }
                }
                await api.shopperCustomers.createCustomerPaymentInstrument({
                    body,
                    parameters: {customerId: customer.customerId}
                })

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer()
            },

            /**
             * Remove a saved payment instrument.
             *
             * @param {string} paymentInstrumentId - The id of the payment payment instrument.
             */
            async removeSavedPaymentInstrument(paymentInstrumentId) {
                // This SDK method must be called with `true` as second argument to avoid an error in
                // the sdk where it tries parsing json from an empty http response.
                await api.shopperCustomers.deleteCustomerPaymentInstrument(
                    {
                        parameters: {customerId: customer.customerId, paymentInstrumentId}
                    },
                    true
                )

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer()
            },

            /**
             * Remove a saved address.
             *
             * @param {string} addressId - The id of the saved address.
             */
            async removeSavedAddress(addressId) {
                // This SDK method must be called with `true` as second argument to avoid an error in
                // the sdk where it tries parsing json from an empty http response.
                await api.shopperCustomers.removeCustomerAddress(
                    {
                        parameters: {customerId: customer.customerId, addressName: addressId}
                    },
                    true
                )

                // This endpoint does not return the updated customer object, so we manually fetch it
                await self.getCustomer()
            },

            /**
             * Extracts first MY_ACCOUNT_ORDERS_LIMIT non-failed customer orders.
             * We might have to execute more than 1 extraction of orders due to filtering by failed order on SFCC side
             * @param {Object} params - order request parameters
             @ @return {Object} response with the first MY_ACCOUNT_ORDERS_LIMIT non-failed orders
             */
            async getCustomerOrders(params) {
                const limit = MY_ACCOUNT_ORDERS_LIMIT
                let offset = 0
                let response
                let initialLoadedOrders
                do {
                    initialLoadedOrders = await api.shopperCustomers.getCustomerOrders({
                        parameters: {customerId: customer?.customerId, offset, limit, ...params}
                    })
                    if (!initialLoadedOrders) {
                        return initialLoadedOrders
                    }
                    if (!response) {
                        response = initialLoadedOrders
                        // initialize data array in case there are no valid orders in the response
                        if (!response.data) {
                            response.data = []
                        }
                    } else if (initialLoadedOrders.data) {
                        // accumulate order data from all the requests in a single response object
                        // accumulation would be done only in case of existing data array inside the new response
                        response.data = response.data.concat(initialLoadedOrders.data)
                        // remove orders in case we have extracted more than 10 orders
                        response.data = response.data.slice(0, limit)
                    }
                    if (initialLoadedOrders) {
                        offset = initialLoadedOrders.limit + initialLoadedOrders.offset
                    }
                    // continue order extraction until we tried extracting all the orders
                    // or the total amount of extracted orders has reached the required limit
                    // or the returned limit is 0 - meaning there is no other order in SFCC
                } while (
                    !(
                        response.limit + response.offset >= response.total ||
                        response.data.length >= response.limit ||
                        initialLoadedOrders.limit === 0
                    )
                )
                return response
            },

            async getOrder(orderNo) {
                const response = await api.shopperOrders.getOrder({
                    parameters: {orderNo}
                })
                return response
            },

            async getCustomerOrderProductsDetail(ids) {
                const response = await api.shopperProducts.getProducts({
                    parameters: {ids: ids.join(',')}
                })

                const productMap = response.data.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})

                return productMap
            }
        }
    }, [api, customer, setCustomer, onLogin, onLogout, customPreferences?.doubleOptInEnabled])

    return self
}
